import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaExclamationTriangle } from 'react-icons/fa';

import { AMBER, DiffCountPill, SectionLabel } from '../styles';

// ---------------------------------------------------------------------------
// Topo do rail esquerdo (v2 do handoff, 2026-09-15): card "RESUMO DO PERFIL"
// + badge "Alterações +X −Y" que abre o modal de diff. Substituiu a antiga
// coluna direita de 288px — a lista de sensíveis virou uma linha única no
// card (sem itens clicáveis), e a lista de alterações virou modal.
// ---------------------------------------------------------------------------

const Block = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 14px 14px 12px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SummaryCard = styled.div`
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 12px;
   padding: 12px;
`;

const BigNumberRow = styled.div`
   display: flex;
   align-items: baseline;
   gap: 6px;

   strong {
      font-size: 26px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: ${({ theme }) => theme.colors.textPrimary};
      line-height: 1.1;
   }
   span {
      font-size: 12.5px;
      color: ${({ theme }) => theme.colors.textMuted};
      white-space: nowrap;
   }
`;

const SummaryMeta = styled.div`
   margin-top: 4px;
   font-size: 12px;
   color: ${({ theme }) => theme.colors.textMuted};
`;

const SensitiveLine = styled.div`
   margin-top: 6px;
   display: flex;
   align-items: center;
   gap: 5px;
   font-size: 12px;
   font-weight: 700;
   color: ${AMBER.text};

   svg {
      width: 13px;
      height: 13px;
      flex: 0 0 auto;
   }
`;

// Badge "Alterações" — botão local com estado de domínio (G.4.1: o hover
// declara o próprio background).
const DiffBadge = styled.button`
   appearance: none;
   font-family: inherit;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   width: 100%;
   min-height: 38px;
   padding: 0 10px;
   border-radius: 10px;
   background: #fff;
   border: 1px solid
      ${({ $dirty }) => ($dirty ? 'rgba(232, 140, 20, 0.45)' : 'rgba(0, 0, 0, 0.14)')};

   font-size: 12px;
   font-weight: 800;
   color: ${({ theme }) => theme.colors.textPrimary};

   &:hover:not(:disabled) {
      background: #fff;
      border-color: ${({ theme }) => theme.colors.actionWrite};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

export default function RailSummary({
   summary,
   sensitiveCount,
   addedCount,
   removedCount,
   onOpenDiff,
}) {
   const dirty = addedCount + removedCount > 0;

   return (
      <Block>
         <SectionLabel>Resumo do perfil</SectionLabel>
         <SummaryCard>
            <BigNumberRow>
               <strong>{summary.granted}</strong>
               <span>de {summary.total} permissões</span>
            </BigNumberRow>
            <SummaryMeta>
               {summary.completeModules} módulos completos ·{' '}
               {summary.emptyModules} sem acesso
            </SummaryMeta>
            {sensitiveCount > 0 && (
               <SensitiveLine>
                  <FaExclamationTriangle />
                  {sensitiveCount}{' '}
                  {sensitiveCount === 1
                     ? 'permissão sensível concedida'
                     : 'permissões sensíveis concedidas'}
               </SensitiveLine>
            )}
         </SummaryCard>

         <DiffBadge
            type="button"
            $dirty={dirty}
            onClick={onOpenDiff}
            title="Ver as alterações não salvas"
         >
            Alterações
            <DiffCountPill $active={dirty}>
               +{addedCount} −{removedCount}
            </DiffCountPill>
         </DiffBadge>
      </Block>
   );
}

RailSummary.propTypes = {
   summary: PropTypes.shape({
      total: PropTypes.number,
      granted: PropTypes.number,
      completeModules: PropTypes.number,
      emptyModules: PropTypes.number,
   }).isRequired,
   sensitiveCount: PropTypes.number.isRequired,
   addedCount: PropTypes.number.isRequired,
   removedCount: PropTypes.number.isRequired,
   onOpenDiff: PropTypes.func.isRequired,
};
